import React, { useCallback, useState } from 'react';
import { toSentenceCase } from '../../utils/util';
import clsx from 'clsx';
import { phoneCodes as defaultPhoneCodes } from '../../constants/phone-codes';

interface PhoneFieldProps {
  placeholder: string;
  error?: string | null;
  onChangeCode: (text: string) => void;
  onChangeNumber: (text: string) => void;
  name: string;
  countryCode: string;
  phoneNumber: string;
}

export const PhoneField: React.FC<PhoneFieldProps> = ({
  placeholder,
  error,
  onChangeCode,
  onChangeNumber,
  name,
  countryCode,
  phoneNumber,
}) => {
  const phoneCodes = defaultPhoneCodes;
  const countryCodeOptions = phoneCodes.map((country) => ({
    label: `${country.e} ${country.n}`,
    value: country.d,
    code: country.d,
  }));

  const simpleOptions = countryCodeOptions.map((option, index) => (
    <option
      label={`${option.label.split(' ')[0]} ${option.code}`}
      value={option.value}
      key={index}
    >
      {`${option.label.split(' ')[0]} ${option.code}`}
    </option>
  ));

  const verboseOptions = countryCodeOptions.map((option, index) => (
    <option label={option.label} value={option.value} key={index}>
      {option.label}
    </option>
  ));

  const handleChangeNumber = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChangeNumber(e.target.value);
    },
    [onChangeNumber],
  );
  const [isVerbose, setVerbose] = useState(false);

  const handleChangeCountry = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      setVerbose(false);
      onChangeCode(e.target.value);
    },
    [onChangeCode],
  );

  return (
    <div className="w-full flex flex-col gap-2 mt-5" id={`${name}-field`}>
      <div
        className={clsx(
          'w-full py-3 px-5 flex flex-col bg-gray-100 rounded-md relative',
          {
            'border-2 border-transparent focus-within:border-2 focus-within:border-blue-950':
              !error,
            'border-2 border-red-200': error,
          },
        )}
      >
        <label className="font-sans text-gray-250 font-regular text-sm">
          {toSentenceCase(name)}*
        </label>
        <div className="outline-none flex flex-row items-center text-base md:text-base border-none w-full pt-1.5 m-0 bg-gray-100">
          <select
            className="h-full w-[8ch] bg-transparent focus:outline-none"
            onChange={handleChangeCountry}
            onBlurCapture={() => setVerbose(false)}
            onPointerDownCapture={() => setVerbose(true)}
            value={countryCode}
          >
            {isVerbose ? verboseOptions : simpleOptions}
          </select>
          <input
            type="tel"
            className="outline-none text-base ml-2 md:text-base border-none w-full m-0 bg-gray-100"
            placeholder={placeholder}
            required={true}
            onChange={handleChangeNumber}
            name={name}
            value={phoneNumber}
          />
        </div>
      </div>
      {error ? <span className="text-red-200 pl-5">{error}</span> : null}
    </div>
  );
};
